import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const CARD: React.CSSProperties = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "14px 18px" };

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  queued: { bg: "#e8f0f3", fg: "#2a6f8a" },
  approved: { bg: "#e8f4ee", fg: "#1f8a5b" },
  awaiting_approval: { bg: "#fbf2dc", fg: "#9a6a12" },
  sent: { bg: "#e8f4ee", fg: "#1f8a5b" },
};

export function Macros() {
  const qc = useQueryClient();
  const macros = useQuery({ queryKey: ["macros"], queryFn: api.macros });
  const bulk = useQuery({ queryKey: ["bulk"], queryFn: api.bulkSends });
  const [macroId, setMacroId] = useState("");
  const [recipients, setRecipients] = useState("");

  const createBulk = useMutation({
    mutationFn: () => api.createBulk(macroId, Number(recipients) || 0),
    onSuccess: () => { setRecipients(""); qc.invalidateQueries({ queryKey: ["bulk"] }); },
  });
  const approve = useMutation({
    mutationFn: (id: string) => api.approveBulk(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["bulk"] }),
  });

  if (macros.isLoading || bulk.isLoading) return <Loading />;
  if (macros.error) return <ErrorNote error={macros.error} />;
  if (bulk.error) return <ErrorNote error={bulk.error} />;
  const macroList = macros.data ?? [];
  const sends = bulk.data ?? [];

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Macros &amp; bulk messages</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>Reusable reply templates and mass sends. Sends to more than 50 recipients need a second approver; approved sends are queued for the delivery pipeline.</div>
      </div>

      {(createBulk.isError || approve.isError) && (
        <div style={{ ...CARD, background: "#fbe9e7", boxShadow: "inset 0 0 0 1px #f3d3ce", color: "#b3261e", fontSize: 12.5 }}>
          {createBulk.isError
            ? `Couldn't queue the send — ${createBulk.error instanceof Error ? createBulk.error.message : String(createBulk.error)}`
            : `Couldn't approve — ${approve.error instanceof Error ? approve.error.message : String(approve.error)}. A bulk send must be approved by someone other than the requester.`}
        </div>
      )}

      <div style={CARD}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>New bulk send</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <select value={macroId} onChange={(e) => setMacroId(e.target.value)} style={{ fontSize: 13, padding: "7px 10px", border: "1px solid #e2e0d8", borderRadius: 7, minWidth: 220 }}>
            <option value="">Choose a macro…</option>
            {macroList.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <input value={recipients} onChange={(e) => setRecipients(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Recipients" inputMode="numeric" style={{ fontSize: 13, padding: "7px 10px", border: "1px solid #e2e0d8", borderRadius: 7, width: 120 }} />
          <button
            onClick={() => createBulk.mutate()}
            disabled={!macroId || !recipients || createBulk.isPending}
            style={{ fontSize: 12.5, fontWeight: 600, color: "#fff", background: macroId && recipients ? "#006747" : "#b7c3bd", border: 0, borderRadius: 7, padding: "8px 12px", cursor: macroId && recipients ? "pointer" : "default" }}
          >
            Queue send
          </button>
          {Number(recipients) > 50 && <span style={{ fontSize: 11.5, color: "#9a6a12" }}>Needs a second approver</span>}
        </div>
      </div>

      <div style={CARD}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Bulk send queue</div>
        {sends.length === 0 ? (
          <div style={{ color: "#8a8e88", fontSize: 13 }}>No bulk sends yet.</div>
        ) : (
          sends.map((s) => {
            const tone = STATUS_TONE[s.status] ?? { bg: "#f0efe9", fg: "#6b706a" };
            return (
              <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "9px 0", borderTop: "1px solid #f4f3ee" }}>
                <div style={{ fontSize: 12.5 }}>
                  <span style={{ fontWeight: 500 }}>{s.macro}</span>
                  <span style={{ color: "#8a8e88" }}> · {s.recipients} recipients · by {s.requestedBy}{s.approvedBy ? ` · approved by ${s.approvedBy}` : ""}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: tone.bg, color: tone.fg }}>{s.status.replace(/_/g, " ")}</span>
                  {s.needsApproval && (
                    <button
                      onClick={() => approve.mutate(s.id)}
                      disabled={approve.isPending}
                      title="Approve as a second approver (must differ from the requester)"
                      style={{ fontSize: 11.5, fontWeight: 600, color: "#006747", background: "#fff", border: "1px solid #cfe0d6", borderRadius: 7, padding: "5px 10px", cursor: "pointer" }}
                    >
                      Approve
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div style={CARD}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Reply macros ({macroList.length})</div>
        {macroList.length === 0 ? (
          <div style={{ color: "#8a8e88", fontSize: 13 }}>No macros yet.</div>
        ) : (
          macroList.map((m) => (
            <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid #f4f3ee", fontSize: 12.5 }}>
              <span style={{ fontWeight: 500 }}>{m.name}</span>
              <span style={{ color: "#8a8e88" }}>{m.category ?? "—"} · {m.uses} uses</span>
            </div>
          ))
        )}
      </div>
    </Page>
  );
}
